import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AssistantRuntimeProvider,
  ExportedMessageRepository,
  ThreadAssistantMessagePart,
  useLocalRuntime,
  useAui,
  type ChatModelAdapter,
  type ThreadMessageLike
} from '@assistant-ui/react';
import { ChatSupportServerError, getChatSupportErrorMessage } from './chatSupportErrors';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';

const API_URL = process.env.REACT_APP_OYA_CARE_API_URL || 'https://api-qa.oya.care';
const ANONYMOUS_TOKEN = 'oya-care';

export type ChatSupportAction = { title: string; action: string };
export type ChatSupportChatResponse = { message: string; actions?: ChatSupportAction[] };

async function fetchWithErrorBody(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body?.message ?? '') as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res;
}

export function postChatSupportMessage({
  message,
  sessionId,
  signal
}: {
  message: string;
  sessionId: string;
  signal?: AbortSignal;
}): Promise<ChatSupportChatResponse> {
  return fetchWithErrorBody(`${API_URL}/chat-support/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ANONYMOUS_TOKEN}` },
    body: JSON.stringify({ message, sessionId }),
    signal
  }).then(res => res.json());
}

export function postChatSessionUpdate({
  sessionId,
  token
}: {
  sessionId: string;
  token: string;
}): Promise<ChatSupportChatResponse> {
  return fetchWithErrorBody(`${API_URL}/chat-support/sessions/${sessionId}/update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
  }).then(res => res.json());
}

const chatHistory = new Map<string, ThreadMessageLike[]>();
let persistedSessionId: string | null = null;
let sessionPromise: Promise<string | null> | null = null;
let sessionError: (Error & { status?: number }) | null = null;
let welcomeMessage: string | null = null;

function ensureSession({ token, sessionSource }: { token?: string | null; sessionSource?: string }): Promise<string | null> {
  if (persistedSessionId) return Promise.resolve(persistedSessionId);

  if (!sessionPromise) {
    sessionPromise = fetchWithErrorBody(`${API_URL}/chat-support/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ sessionSource })
    })
      .then(res => res.json())
      .then(data => {
        persistedSessionId = data.sessionId as string;
        welcomeMessage = data.message as string;
        sessionError = null;
        return persistedSessionId;
      })
      .catch((err: Error & { status?: number }) => {
        sessionError = err;
        sessionPromise = null;
        return null;
      });
  }

  return sessionPromise;
}

export function clearChatSession() {
  chatHistory.clear();
  persistedSessionId = null;
}

export function getPersistedSessionId(): string | null {
  return persistedSessionId;
}

export function useSelectedToken(): string {
  const [cookies] = useCookies(['oya_oyana_access_token']);
  return cookies.oya_oyana_access_token && cookies.oya_oyana_access_token.length > 0
    ? cookies.oya_oyana_access_token
    : ANONYMOUS_TOKEN;
}

export function useEnsureSession(): { isLoading: boolean } {
  const selectedToken = useSelectedToken();
  const [searchParams] = useSearchParams();
  const sessionSource = searchParams.get('sessionSource') ?? 'agendamento';

  const [isLoading, setIsLoading] = useState(persistedSessionId === null);

  useEffect(() => {
    let cancelled = false;

    ensureSession({ token: selectedToken, sessionSource }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return { isLoading };
}

// cria a bolha da assistente em "running" (mostra o typing indicator) e preenche quando a resposta chega
function useRunAssistantResponse() {
  const aui = useAui();

  return (getResponse: () => Promise<ChatSupportChatResponse>) => {
    const thread = aui.thread();
    thread.resumeRun({
      parentId: thread.getState().messages.at(-1)?.id ?? null,
      stream: async function* () {
        let response: ChatSupportChatResponse;
        try {
          response = await getResponse();
        } catch (err) {
          const { message, status } = err as Error & { status?: number };
          response = { message: getChatSupportErrorMessage(message, status ?? 500) };
        }
        yield {
          content: [{ type: 'text' as const, text: response.message ?? '' }],
          metadata: { custom: { actions: response.actions ?? [] } }
        };
      }
    });
  };
}

export function useUpdateChatSession() {
  const runAssistantResponse = useRunAssistantResponse();

  return (token: string = ANONYMOUS_TOKEN) =>
    runAssistantResponse(async () => {
      const sessionId = await ensureSession({ token });
      if (!sessionId) {
        return { message: getChatSupportErrorMessage(sessionError?.message ?? '', sessionError?.status ?? 500) };
      }

      try {
        return await postChatSessionUpdate({ sessionId, token });
      } catch (err) {
        const { message, status } = err as Error & { status?: number };
        return { message: getChatSupportErrorMessage(message, status ?? 500) };
      }
    });
}

function getStoredMessages(sessionId: string): ThreadMessageLike[] {
  return chatHistory.get(sessionId) ?? [];
}

function persistMessages(sessionId: string, messages: ThreadMessageLike[]) {
  chatHistory.set(sessionId, messages);
}

export function AssistantUIProvider({ children }: Readonly<{ children: ReactNode }>) {
  const navigate = useNavigate();
  const selectedToken = useSelectedToken();

  const adapter = useMemo<ChatModelAdapter>(
    () => ({
      async run({ messages, abortSignal }) {
        const sessionId = await ensureSession({ token: selectedToken });

        if (!sessionId) {
          const err = sessionError;
          return {
            content: [{ type: 'text' as const, text: getChatSupportErrorMessage(err?.message ?? '', err?.status ?? 500) }]
          };
        }

        const lastContent = messages[messages.length - 1].content[0];
        const message = lastContent && 'text' in lastContent ? lastContent.text : 'Mensagem não suportada';

        let chatErrorMessage = '';

        const data = await postChatSupportMessage({ message, sessionId, signal: abortSignal }).catch(
          async (err: Error & { status?: number }) => {
            if (err.message === ChatSupportServerError.CHAT_DISABLED) {
              navigate('/');
              return null;
            }
            chatErrorMessage = getChatSupportErrorMessage(err.message, err.status ?? 500);
            return null;
          }
        );

        if (!data) {
          return { content: [{ type: 'text' as const, text: chatErrorMessage }] };
        }
        const responseAdapter: ThreadAssistantMessagePart = { type: 'text', text: data.message ?? '' };
        return {
          content: [responseAdapter],
          metadata: { custom: { actions: data.actions ?? [] } }
        };
      }
    }),
    []
  );

  const runtime = useLocalRuntime(adapter);

  useEffect(() => {
    let cancelled = false;

    ensureSession({ token: selectedToken }).then(sessionId => {
      if (cancelled || !sessionId) return;

      const stored = getStoredMessages(sessionId);
      if (stored.length > 0) {
        runtime.thread.import(ExportedMessageRepository.fromArray(stored));
      } else if (welcomeMessage) {
        runtime.thread.import(
          ExportedMessageRepository.fromArray([
            {
              id: 'welcome',
              role: 'assistant',
              content: [{ type: 'text', text: welcomeMessage }],
              status: { type: 'complete', reason: 'stop' },
              createdAt: new Date()
            }
          ])
        );
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return runtime.thread.subscribe(() => {
      if (!persistedSessionId) return;
      const { messages } = runtime.thread.getState();
      const complete = messages.filter(
        m => m.role !== 'assistant' || (m as { status?: { type: string } }).status?.type === 'complete'
      );
      persistMessages(persistedSessionId, complete);
    });
  }, []);

  return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
}
